import React from 'react'
import {
  FormControl,
  Avatar,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { grey } from '@mui/material/colors'
import { useTheme } from '@mui/material/styles'
import { get, noop } from 'lodash'
import PropTypes from 'prop-types'
import { extractNameInitials } from 'utils/string'
import { isValidHex } from 'utils/validations'
import HHTextField from 'components/form-fields/v5/HHTextField'
import SelectItem from 'components/form-fields/v5/SelectItem'
import { Controller, useFormContext } from 'react-hook-form'

const DesktopRoutePicker = ({
  control,
  name,
  onChange = noop,
  readOnly = false,
  routes = [],
  menuSx,
  paperSx = {},
  styleSx = {},
  rules = {},
  showStopCount = true,
  clearable = false,
  onClear,
  // Default keeps the legacy 32 px avatar that every other consumer renders.
  // Master-route header passes 24 to match the prototype's 24 px closed-pill
  // avatar — smaller pill fits the 32 px toolbar baseline.
  avatarSize = 32,
  ...rest
}) => {
  const theme = useTheme()
  const { watch } = useFormContext()
  const value = watch(name)
  return (
    <FormControl fullWidth sx={{ position: 'relative' }}>
      {!value && <InputLabel sx={{ height: '40px', lineHeight: '.8rem' }}>Select a route</InputLabel>}
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => {
          const { ref, onChange: onChangeHookForm, onBlur: onBlurHookForm, ..._rest } = field
          const handleChange = e => {
            onChangeHookForm(e)
            onChange(e.target.value)
          }
          const renderValue = val => {
            if (!val || !val?.length) {
              return 'Select a route'
            }
            if (Array.isArray(val)) {
              return val.join(', ')
            }
            return val
          }
          const handleClear = e => {
            e.stopPropagation()
            onChangeHookForm({ target: { value: '' } })
            if (onClear) onClear()
          }
          const showClear = clearable && field.value
          return (
            <HHTextField
              {...field}
              sx={{
                '& .MuiOutlinedInput-root .MuiListItemAvatar-root': {
                  minWidth: '30px',
                  position: 'relative',
                  left: '-8px',
                },
                ...styleSx,
              }}
              onBlur={onBlurHookForm}
              readOnly={readOnly}
              onChange={handleChange}
              fullWidth
              select
              inputRef={ref}
              variant="outlined"
              displayEmpty
              renderValue={renderValue}
              placeholder="Select a route"
              InputProps={
                showClear
                  ? {
                      endAdornment: (
                        <InputAdornment position="end" sx={{ mr: 2 }}>
                          <IconButton size="small" onClick={handleClear} edge="end">
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
              SelectProps={{
                MenuProps: {
                  sx: { zIndex: theme.zIndex.snackbar, ...menuSx },
                  PaperProps: {
                    sx: { maxHeight: 'calc(100vh - 80px)', ...paperSx },
                  },
                  alignment: {
                    getContentAnchorEl: null,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  },
                },
              }}
              {..._rest}
              {...rest}
            >
              {routes.map(val => {
                const routeName = get(val, 'name', '')
                const bgcolor = get(val, 'color', grey[100])
                const formattedBgcolor = isValidHex(bgcolor) ? bgcolor : '#000000'
                const color = theme.palette.getContrastText(formattedBgcolor)
                const id = get(val, 'id')
                const initials = extractNameInitials(routeName)
                const stopsCount = get(val, `stopCount`, 0)
                return (
                  <SelectItem disableGutters value={id}>
                    <ListItem disabbleGutters disablePadding>
                      <ListItemAvatar sx={{ minWidth: avatarSize <= 24 ? '32px' : '40px' }}>
                        <Avatar
                          sx={{
                            width: `${avatarSize}px`,
                            height: `${avatarSize}px`,
                            bgcolor: formattedBgcolor,
                            color,
                            fontSize: avatarSize <= 24 ? 12 : undefined,
                          }}
                        >
                          <Typography variant="h5" fontWeight={400} sx={{ fontSize: 'inherit' }}>
                            {initials}
                          </Typography>
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={routeName}
                        secondary={showStopCount ? `${stopsCount} stops` : ''}
                        slotProps={{
                          primary: { noWrap: true },
                        }}
                      />
                    </ListItem>
                  </SelectItem>
                )
              })}
            </HHTextField>
          )
        }}
      />
    </FormControl>
  )
}

DesktopRoutePicker.propTypes = {
  routes: PropTypes.array.isRequired,
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  menuSx: PropTypes.object,
  paperSx: PropTypes.object,
  styleSx: PropTypes.object,
  showStopCount: PropTypes.bool,
  clearable: PropTypes.bool,
  onClear: PropTypes.func,
  rules: PropTypes.object,
  avatarSize: PropTypes.number,
}

export default DesktopRoutePicker
